/**
 * The promotions pages' pure reading — names, placement on the timeline, the findings, the order
 * of the mechanics. `docs/adr/8004`.
 *
 * Every catalogue key is spelled in full in a literal record, so the coverage check sees each one.
 * Nothing here ranks a mechanic: the panel lists them in the vocabulary's declared order and says
 * how many intervals include no difference, never which mechanic did best (reqs/07 §3.4 — the
 * promotion simulation's decisions are open).
 */

import {
  PROMOTION_MECHANICS, type LabelMechanic, type MechanicImpact, type MechanicReading,
  type PromotionMechanic, type PromotionWindow, type WeekLabel, type WindowClass,
} from '@/api/pending/promotions';

import { periodDays, type PromotionPeriod } from './scope';
import { displayNumberFormat } from '@/lib/format';

/** A vocabulary code's name. */
export const MECHANIC_NAME_KEY: Readonly<Record<PromotionMechanic, string>> = {
  multi_buy: 'postseason.promotions.mechanic.multi_buy',
  second_item_percent_off: 'postseason.promotions.mechanic.second_item_percent_off',
  article_percent_off: 'postseason.promotions.mechanic.article_percent_off',
};

/**
 * The tills' class, described (bata-data ADR 8006 §6, ADR 8011). `LINE_DEPTH` (the basket's
 * second-item line) and `ARTICLE_LINE_DEPTH` (an article's markdown step, the sale zone) take
 * `{depth}`.
 */
export const WINDOW_CLASS_KEY: Readonly<Record<WindowClass, string>> = {
  FREE_ITEM: 'postseason.promotions.class.FREE_ITEM',
  LINE_DEPTH: 'postseason.promotions.class.LINE_DEPTH',
  ARTICLE_LINE_DEPTH: 'postseason.promotions.class.ARTICLE_LINE_DEPTH',
  LOW: 'postseason.promotions.class.LOW',
};

/** A class this build has no words for: its code, verbatim, in a sentence that says so (`{code}`). */
export const WINDOW_CLASS_UNKNOWN_KEY = 'postseason.promotions.class.unknown';

/** Bata's weekly label's mechanic, as the headline parse reads it (bata-data ADR 8006 §2). */
export const LABEL_MECHANIC_KEY: Readonly<Record<LabelMechanic, string>> = {
  MULTIBUY_3X2: 'postseason.promotions.label.MULTIBUY_3X2',
  MULTIBUY_2X1: 'postseason.promotions.label.MULTIBUY_2X1',
  SECOND_PAIR_PCT: 'postseason.promotions.label.SECOND_PAIR_PCT',
  SECOND_ARTICLE_PCT: 'postseason.promotions.label.SECOND_ARTICLE_PCT',
  SALE_ZONE_EOSS: 'postseason.promotions.label.SALE_ZONE_EOSS',
};

/** The agreement's word — with a glyph, never a colour alone (§12.6). */
export const AGREEMENT_KEY = {
  agree: 'postseason.promotions.agreement.agree',
  disagree: 'postseason.promotions.agreement.disagree',
  none: 'postseason.promotions.agreement.none',
} as const;

/** Whether Bata's weekly label and the tills agree on the window — `none`: no label to compare. Pure. */
export function agreementOf(window: PromotionWindow): keyof typeof AGREEMENT_KEY {
  return window.labelAgrees === null ? 'none' : window.labelAgrees ? 'agree' : 'disagree';
}

/** Which note a mechanic carries beside its name, or none. */
export type MechanicNote = 'unregistered' | 'uncoded' | null;

/**
 * How a window's mechanic is said: the vocabulary's name only when the code is REGISTERED;
 * otherwise the mart's descriptive class with a neutral note — «not yet in the approved
 * vocabulary» for a proposed code, «no mechanic code» for a class the vocabulary has none for
 * (a low-depth run). Never hidden, never relabelled. Pure.
 */
export function mechanicNote(mechanic: MechanicReading): MechanicNote {
  if (mechanic.registered && mechanic.code !== null) return null;
  return mechanic.code === null ? 'uncoded' : 'unregistered';
}

export const MECHANIC_NOTE_KEY: Readonly<Record<Exclude<MechanicNote, null>, string>> = {
  unregistered: 'postseason.promotions.note.unregistered',
  uncoded: 'postseason.promotions.note.uncoded',
};

/** Where a window sits on the period's strip, in shares of the period (0–1), clipped to it. Pure. */
export function stripPlacement(window: { readonly start: string; readonly end: string }, period: PromotionPeriod): {
  readonly offset: number;
  readonly width: number;
} {
  const span = periodDays(period);
  const from = Date.parse(`${period.from}T00:00:00Z`);
  const day = (iso: string): number => Math.round((Date.parse(`${iso}T00:00:00Z`) - from) / 86_400_000);
  const first = Math.max(0, day(window.start));
  const last = Math.min(span - 1, day(window.end));
  if (span <= 0 || last < first) return { offset: 0, width: 0 };
  return { offset: first / span, width: (last - first + 1) / span };
}

/** What the timeline's sentence states. Pure. */
export interface WindowsFinding {
  readonly windows: number;
  /** Windows a weekly label could be compared with. */
  readonly labelled: number;
  readonly disagree: number;
  /** Windows whose mechanic is not (yet) in the approved vocabulary. */
  readonly unregistered: number;
  /** Days of the period inside some window. */
  readonly daysInWindows: number;
}

export function windowsFinding(windows: readonly PromotionWindow[], period: PromotionPeriod): WindowsFinding {
  const covered = new Set<number>();
  for (const window of windows) {
    const { offset, width } = stripPlacement(window, period);
    const span = periodDays(period);
    const first = Math.round(offset * span);
    for (let day = first; day < first + Math.round(width * span); day += 1) covered.add(day);
  }
  return {
    windows: windows.length,
    labelled: windows.filter((window) => window.labelAgrees !== null).length,
    disagree: windows.filter((window) => window.labelAgrees === false).length,
    unregistered: windows.filter((window) => mechanicNote(window.mechanic) !== null).length,
    daysInWindows: covered.size,
  };
}

/**
 * The mechanic rows in the vocabulary's DECLARED order — then the rows with no code, then by the
 * mart's group key and the channel. Never by a figure: the order says nothing about which did
 * better. Pure.
 */
export function orderedMechanics(rows: readonly MechanicImpact[]): readonly MechanicImpact[] {
  const rank = (row: MechanicImpact): number => (row.mechanic.code === null ? PROMOTION_MECHANICS.length : PROMOTION_MECHANICS.indexOf(row.mechanic.code));
  const text = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
  return [...rows].sort((a, b) => (rank(a) - rank(b)) || text(a.group, b.group) || text(a.channel, b.channel));
}

/** What the panel's chart title states: how many intervals include no difference. Pure. */
export function intervalFinding(rows: readonly MechanicImpact[]): { readonly withInterval: number; readonly includeZero: number } {
  const bounds = rows.flatMap((row) => (row.netUplift.low === null || row.netUplift.high === null ? [] : [[row.netUplift.low, row.netUplift.high] as const]));
  return {
    withInterval: bounds.length,
    includeZero: bounds.filter(([low, high]) => low <= 0 && high >= 0).length,
  };
}

/** A signed percentage, «+4%», «−2.1%» — a change against the neighbours, so its sign is said. Pure. */
export function signedPercent(value: number, locale: string, fractionDigits = 1): string {
  return displayNumberFormat(locale, {
    style: 'percent', signDisplay: 'exceptZero', minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits,
  }).format(value);
}

/** A rate change (a fraction) in percentage points, signed: 0.021 → «+2.1». The unit is the caller's key. Pure. */
export function signedPoints(value: number, locale: string, fractionDigits = 1): string {
  return displayNumberFormat(locale, {
    signDisplay: 'exceptZero', minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits,
  }).format(value * 100);
}

/** A label's known mechanic, else `null` (the raw parse code is shown instead). */
export const labelMechanicKey = (label: WeekLabel): string | null => (label.labelMechanic === null ? null : LABEL_MECHANIC_KEY[label.labelMechanic]);

/** A plain decimal written as text: optional sign, digits, optional fraction. */
const DECIMAL = /^-?\d+(\.\d+)?$/;

/** A decimal amount's position for SORTING a column only — never added, never shown. Pure. */
export function decimalSortValue(amount: string | null): number | null {
  return amount === null || !DECIMAL.test(amount) ? null : Number(amount);
}

/**
 * An exact decimal amount (the platform's TEXT, ADR 8012) in its own currency, for display. Handed
 * to `Intl.NumberFormat` as the string it is — never parsed to a float, never added — so the
 * figure shown is the figure landed; a negative carries Intl's minus sign, never parentheses
 * (ADR 9061), and a negative zero reads plain. A value that is not a plain decimal is shown
 * verbatim rather than guessed at. Pure.
 */
export function formatDecimalMoney(amount: string, currency: string, locale: string): string {
  if (!DECIMAL.test(amount)) return amount;
  try {
    // ES2023 Intl: a numeric string is formatted exactly. `as unknown as number` only for the lib types.
    // The minimum is stated, not left to the currency's CLDR digits: those differ between ICU
    // versions (COP is 2 in older data, 0 in CLDR 48), so the same column would read «.5» in one
    // browser and «.50» in another. Two decimals everywhere.
    return displayNumberFormat(locale, {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'negative',
    })
      .format(amount as unknown as number);
  } catch {
    return amount;
  }
}
